import { Plugin } from 'ckeditor5/src/core';
import { Widget, toWidget, toWidgetEditable } from 'ckeditor5/src/widget';
import { NoteCommand } from './mgnlnotecommand';

/**
 * The note editing feature.
 */
export default class MgnlNoteEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'MgnlNoteEditing' as const;
	}

	/**
	 * @inheritDoc
	 */
	public static get requires() {
		return [Widget] as const;
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		console.log('MgnlNoteEditing initializing');

		this._defineSchema();
		this._defineConverters();
		this._defineCommands();

		console.log('MgnlNoteEditing initialized');
	}

	/**
	 * Defines the note schema.
	 */
	private _defineSchema(): void {
		console.log('Defining note schema');

		const schema = this.editor.model.schema;

		// Define the 'note' element in the model
		schema.register('note', {
			inheritAllFrom: '$block',
			allowAttributes: ['noteType'],
		});

		// Define the 'noteContent' element that will be editable
		schema.register('noteContent', {
			allowIn: 'note',
			allowContentOf: '$block',
		});

		console.log('Note schema defined');
	}

	/**
	 * Defines the note converters.
	 */
	private _defineConverters(): void {
		console.log('Defining note converters');

		const conversion = this.editor.conversion;

		// Convert from model to view (editing and data view)
		conversion.for('upcast').elementToElement({
			view: {
				name: 'div',
				classes: ['note-callout'],
			},
			model: (viewElement, { writer }) => {
				// Extract note type from class name
				const classList = Array.from(viewElement.getClassNames());
				let noteType = 'info'; // Default to info type

				for (const cls of classList) {
					if (cls.startsWith('note-')) {
						const extractedType = cls.replace('note-', '');
						if (
							extractedType !== 'callout' &&
							extractedType !== 'content'
						) {
							noteType = extractedType;
							break;
						}
					}
				}

				return writer.createElement('note', { noteType });
			},
		});

		conversion.for('upcast').elementToElement({
			view: {
				name: 'div',
				classes: ['note-content'],
			},
			model: 'noteContent',
		});

		// Convert from model to data view
		conversion.for('dataDowncast').elementToElement({
			model: 'note',
			view: (modelElement, { writer }) => {
				const noteType =
					modelElement.getAttribute('noteType') || 'info';
				return writer.createContainerElement('div', {
					class: `note-callout note-${noteType}`,
				});
			},
		});

		conversion.for('dataDowncast').elementToElement({
			model: 'noteContent',
			view: (modelElement, { writer }) => {
				return writer.createContainerElement('div', {
					class: 'note-content',
				});
			},
		});

		// Convert from model to editing view
		conversion.for('editingDowncast').elementToElement({
			model: 'note',
			view: (modelElement, { writer }) => {
				const noteType =
					modelElement.getAttribute('noteType') || 'info';
				const div = writer.createContainerElement('div', {
					class: `note-callout note-${noteType}`,
				});
				return toWidget(div, writer, { label: `${noteType} note` });
			},
		});

		conversion.for('editingDowncast').elementToElement({
			model: 'noteContent',
			view: (modelElement, { writer }) => {
				const div = writer.createEditableElement('div', {
					class: 'note-content',
				});
				return toWidgetEditable(div, writer);
			},
		});

		console.log('Note converters defined');
	}

	/**
	 * Defines the note commands.
	 */
	private _defineCommands(): void {
		console.log('Defining mgnlNote commands');

		// Check if command already exists
		if (!this.editor.commands.get('mgnlNote')) {
			this.editor.commands.add('mgnlNote', new NoteCommand(this.editor));
			console.log('MgnlNote command added');
		} else {
			console.warn('MgnlNote command already exists');
		}
	}
}
